import { Inject, Injectable } from '@angular/core';
import { Base64UtilsService } from 'src/app/core/services/base64-utils.service';
import { WINDOW } from 'src/app/feature/injection-token/window';
import { environment } from 'src/environments/environment';

/**
 * Handles base64 encoded JSON transactions with the local storage service on a by gateway basis.
 *
 */
@Injectable({
	providedIn: 'root',
})
export class LocalStorageService {
	public tenantName: string;
	private readonly _localStorageEnabled: boolean;

	/**
	 * Creates an instance of LocalStorageService.
	 * @param _window our _window
	 * @param _base64UtilsService service to encode/decode base64
	 */
	constructor(@Inject(WINDOW) private _window: Window, private _base64UtilsService: Base64UtilsService) {
		this._localStorageEnabled = this._getLocalStorageStatus();
	}

	/**
	 * Gets the value from local storage for the current gateway.
	 *
	 * The stored value is expected be either be a base64 encoded JSON string or a JSON string itself.
	 * The stored value will be decoded from base64 if it is a base64 string.
	 * Then the decoded value will deserialize from a JSON string before it is returned.
	 *
	 * @param key The name of the local storage key.
	 * The actual key to reference the storage item will be prefixed with the current gateway followed by an underscore.
	 *
	 * @returns The value from local storage or undefined if it does not exist.
	 */
	public getItem<ItemType>(key: string): ItemType {
		if (!this._localStorageEnabled) {
			return;
		}

		const storageValue = this._window.localStorage.getItem(key);

		if (!storageValue) {
			return;
		}

		return this._deserializeValue<ItemType>(storageValue);
	}

	/**
	 * Sets the value in local storage for the current gateway.
	 * Removes the item from local storage if the value is null or undefined
	 *
	 * The value will be serialized to a JSON string.
	 * The JSON string will be encoded to a base64 string.
	 *
	 * The value will be decoded from base64 if it is a base64 string.
	 * The value to be stored will either be a base64 encoded JSON string
	 * or a JSON string itself when using a non-production build.
	 *
	 * @param key The name of the local storage key.
	 * The actual key to reference the storage item will be prefixed with the current gateway followed by an underscore.
	 * @param value The value to be stored.
	 */
	public setItem<ItemType>(key: string, value?: ItemType): void {
		if (!this._localStorageEnabled) {
			return;
		}

		if (!!!value) {
			window.localStorage.removeItem(key);
			return;
		}

		this._window.localStorage.setItem(key, this._serializeValue<ItemType>(value));
	}

	/**
	 * Removes the key and its value from local storage
	 *
	 * @param key The name of the local storage key.
	 * The actual key to reference the storage item will be prefixed with the current gateway followed by an underscore.
	 */
	public removeItem(key: string): void {
		window.localStorage.removeItem(key);
	}

	/**
	 * Determines if localstorage is accessible
	 * @private
	 */
	private _getLocalStorageStatus(): boolean {
		const testString = 'LocalStorageTest';

		try {
			this._window.localStorage.setItem(testString, testString);
			window.localStorage.removeItem(testString);
			return true;
		} catch (e) {
			return false;
		}
	}

	/**
	 * deserializes storageValue (both base64 and json)
	 * @param storageValue
	 * @private
	 */
	private _deserializeValue<ItemType>(storageValue: string): ItemType {
		return JSON.parse(this._base64UtilsService.decode(storageValue)) as ItemType;
	}

	/**
	 * serializes value
	 * @param value
	 * @private
	 */
	private _serializeValue<ItemType>(value: ItemType): string {
		const serializedValue = JSON.stringify(value);
		const encodedValue = this._base64UtilsService.encode(serializedValue);

		return !environment.production ? serializedValue : encodedValue;
	}
}
